import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDelete } from '@/components/ConfirmDelete';
import { toast } from '@/hooks/use-toast';
import { useDeleteTeamMember } from '@/hooksApi';
import { TeamMember } from '@/types';

interface TeamMemberRowProps {
  member: TeamMember;
  onEdit: (member: TeamMember) => void;
  getRoleLabel: (member: TeamMember) => string;
  hasOwnerRole: (member: TeamMember) => boolean;
}

export const TeamMemberRow = ({ member, onEdit, getRoleLabel, hasOwnerRole }: TeamMemberRowProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: deleteTeamMember, isLoading: isDeleting } = useDeleteTeamMember();

  const handleDelete = () => {
    if (isDeleting) return;
    deleteTeamMember(member.uid, {
      onSuccess: () => {
        queryClient.invalidateQueries(['teamMember-users']);
        toast({ title: t('member_removed') });
      },
    });
  };

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
      <div className='p-5 flex items-center gap-4'>
        <div className='h-10 w-10 rounded-full bg-sd-muted flex items-center justify-center shrink-0 text-xs font-semibold text-sd-muted-foreground'>
          {member.first_name[0]}
          {member.last_name[0]}
        </div>
        <div className='flex-1 min-w-0'>
          <h3 className='text-sm font-semibold text-sd-foreground m-0'>
            {member.first_name} {member.last_name}
          </h3>
          <p className='text-sm text-sd-muted-foreground m-0'>{getRoleLabel(member)}</p>
        </div>
        {!hasOwnerRole(member) && (
          <div className='flex items-center gap-1 shrink-0'>
            <button
              onClick={() => onEdit(member)}
              className='p-1.5 rounded-lg text-sd-muted-foreground hover:text-sd-foreground hover:bg-sd-muted transition-colors'
            >
              <Pencil className='h-4 w-4' />
            </button>
            <ConfirmDelete
              title={t('remove_member')}
              description={`"${member.first_name} ${member.last_name}" ${t('will_be_removed_from_team')}`}
              onConfirm={handleDelete}
              disabled={isDeleting}
            />
          </div>
        )}
      </div>
    </div>
  );
};
